import React from 'react';
import { Timeline, Typography, Tag, Card } from 'antd';
import ReactJson from 'react-json-view';

const { Text } = Typography;

const STATUS_THEME = {
    success: {
        dot: '#52c41a',
        tag: 'success',
        classes: 'bg-success-subtle border-success-subtle text-success-emphasis'
    },
    error: {
        dot: '#ff4d4f',
        tag: 'error',
        classes: 'bg-danger-subtle border-danger-subtle text-danger-emphasis'
    },
    warning: {
        dot: '#faad14',
        tag: 'warning',
        classes: 'bg-warning-subtle border-warning-subtle text-warning-emphasis'
    },
    processing: {
        dot: '#1890ff',
        tag: 'processing',
        classes: 'bg-light border-light text-dark'
    }
};

const ProcessTimeline = ({ logs = [], onClose }) => {

    // Fonction pour déterminer le statut visuel
    const getStatus = (log) => {
        if (log.actionType === 'TRANSACTION_SUCCESS') return 'success';
        if (log.stepNumber === 8 || (log.actionType && log.actionType.includes('ERROR'))) return 'error';
        if (log.actionType && log.actionType.includes('FAIL')) return 'warning';
        return 'processing';
    };

    // Cas où aucun log n'est sélectionné ou trouvé
    if (!logs || logs.length === 0) {
        return (
            <Card className="shadow-sm border-0 p-5 text-center bg-white rounded-3">
                <div className="text-end mb-3">
                    <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                </div>
                <i className="fa fa-folder-open-o fa-3x text-muted mb-3 opacity-50"></i>
                <br />
                <Text type="secondary" className="fw-semibold">
                    Aucun parcours à afficher pour cette session
                </Text>
            </Card>
        );
    }

    // Récupération sécurisée de l'ID de session
    const sessionId = logs[0]?.sessionId || 'N/A';

    return (
        <div className="card shadow-sm border-0 p-3 bg-white rounded-3 overflow-hidden animate-fade-in">

            <div className="card-header bg-white border-bottom">
                <div className="d-flex justify-content-between align-items-center m-0">
                    <h6 className="text-start fw-bold text-uppercase fs-6">
                        <i className="fa fa-road me-2"></i>Parcours 
                    </h6>
                    <div>
                        <code className="text-muted fw-normal bg-light px-2 py-1 rounded">
                            ID: {sessionId}
                        </code>
                        <button
                            type="button"
                            className="btn-close shadow-none small"
                            onClick={onClose}
                            aria-label="Fermer"
                        ></button>
                    </div>                      
                    
                </div>
            </div>

            {/* --- CORPS DE LA TIMELINE --- */}
            <div className="p-4 bg-white" style={{ maxHeight: '100vh22', overflowY: 'auto' }}>
                <Timeline mode="alternate">
                    {logs.map((log, index) => {
                        const status = getStatus(log);
                        const theme = STATUS_THEME[status] || STATUS_THEME.processing;

                        // Parsing sécurisé du JSON
                        let jsonData = {};
                        try {
                            jsonData = typeof log.details === 'string' ? JSON.parse(log.details) : log.details;
                        } catch (e) {
                            jsonData = { error: "Format JSON invalide", raw: log.details };
                        }

                        return (
                            <Timeline.Item
                                key={index}
                                color={theme.dot}
                                label={
                                    <div className="text-muted fw-semibold small mt-1" style={{ lineHeight: '1.3' }}>                                        
                                        <div className="text-dark fw-bold">
                                            {new Date(log.createdAt).toLocaleTimeString(undefined, { hour12: false })}
                                        </div>
                                        <div className="text-uppercase" style={{ fontSize: '10px' }}>
                                            {new Date(log.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' } )}
                                        </div>
                                    </div>
                                }
                                // Point personnalisé pour une meilleure visibilité
                                dot={<i className="fa fa-circle" style={{ color: theme.dot, fontSize: '12px' }}></i>}
                            >
                                {/* Bloc de contenu du log */}
                                <div className={`text-start p-3 rounded-2 border-start border-4 shadow-sm mb-3 ${theme.classes}`}
                                    style={{ minWidth: '260px' }}>

                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fw-bold text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.3px' }}>
                                            Étape {log.stepNumber}
                                        </span>
                                        <Tag color={theme.tag} className="m-0 border-0 fw-bold shadow-sm"
                                            style={{ fontSize: '12px', borderRadius: '4px', padding: '2px 8px' }}>
                                            {log.actionType}
                                        </Tag>
                                    </div>

                                    {/* Composant JSON View */}
                                    <div className="mt-2 border rounded bg-white overflow-hidden shadow-sm"
                                        key={`container-${log.sessionId}-${index}`}
                                    style={{ maxHeight: '300px', overflowY: 'auto' }}                                    >
                                        <ReactJson
                                            key={`${log.sessionId}-${index}`} 
                                            src={jsonData}
                                            theme="rgho"
                                            collapsed={true}
                                            name={false}
                                            displayDataTypes={false}
                                            displayObjectSize={false}
                                            enableClipboard={false}
                                            // Ajout de cette prop pour éviter les bugs d'affichage sur les longs textes
                                            //collapseStringsAfterLength={20}
                                            style={{
                                                padding: '12px',
                                                fontSize: '12px',
                                                backgroundColor: 'rgba(248, 249, 250, 0.5)',
                                                fontFamily: 'var(--bs-font-monospace)',
                                                maxHeight: '300px',      
                                                overflowY: 'auto',       /* Active le scroll vertical si nécessaire */
                                                overflowX: 'hidden',     /* Évite le scroll horizontal si possible */
                                            }}
                                        />
                                    </div>
                                </div>
                            </Timeline.Item>
                        );
                    })}
                </Timeline>
            </div>

            {/* --- FOOTER --- */}
            <div className="py-2 text-center text-muted border-top fs-6">
                <i className="fa fa-info-circle me-1 text-primary"></i>
                 Fin du parcours 
            </div>
        </div>
    );
};

export default ProcessTimeline;